import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useId, useMemo, useRef, useState } from 'react';
export function TagPicker({ label, prefix = '', multiple = false, options, value, onAdd, onRemove, normalize, disabled = false, labels, }) {
    const [text, setText] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const baseId = useId();
    const listId = `${baseId}-list`;
    const chosen = new Set(value.map((tag) => tag.id));
    const trimmed = text.trim();
    const matches = useMemo(() => {
        const needle = normalize(trimmed);
        return options
            .filter((option) => !chosen.has(option.id))
            .filter((option) => needle === '' || normalize(option.name).includes(needle))
            .slice(0, 20);
        // `chosen` comes from `value`; both change together.
    }, [options, value, trimmed, normalize]);
    const exact = options.find((option) => normalize(option.name) === normalize(trimmed));
    const items = [
        ...matches.map((option) => ({
            key: option.id,
            label: `${prefix}${option.name}`,
            tag: { id: option.id, name: option.name },
        })),
        ...(trimmed !== '' && !exact
            ? [{ key: 'create', label: labels.create(trimmed), tag: { name: trimmed } }]
            : []),
    ];
    const choose = (index) => {
        const item = items[index];
        if (!item)
            return;
        onAdd(item.tag);
        setText('');
        setOpen(false);
        setActive(0);
        inputRef.current?.focus();
    };
    const onKeyDown = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) {
                setOpen(true);
                setActive(0);
                return;
            }
            const last = items.length - 1;
            setActive((current) => event.key === 'ArrowDown' ? Math.min(last, current + 1) : Math.max(0, current - 1));
        }
        else if (event.key === 'Enter') {
            if (items.length === 0)
                return;
            event.preventDefault();
            choose(open ? active : 0);
        }
        else if (event.key === 'Escape' && open) {
            // Closes the list without closing the dialog or panel around it.
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
        }
        else if (event.key === 'Backspace' && text === '' && value.length > 0) {
            onRemove(value[value.length - 1].id);
        }
    };
    return (_jsxs("div", { className: "tag-picker", "data-multiple": multiple ? 'true' : undefined, children: [value.length > 0 ? (_jsx("ul", { className: "tag-picker__chips", "aria-label": label, children: value.map((tag) => (_jsxs("li", { className: "tag-picker__chip", children: [_jsxs("span", { children: [prefix, tag.name] }), _jsx("button", { type: "button", "aria-label": labels.remove(tag.name), disabled: disabled, onClick: () => onRemove(tag.id), children: "\u00D7" })] }, tag.id))) })) : null, multiple || value.length === 0 ? (_jsxs(_Fragment, { children: [_jsx("input", { ref: inputRef, type: "text", role: "combobox", className: "tag-picker__input", "aria-label": label, "aria-expanded": open, "aria-controls": listId, "aria-autocomplete": "list", "aria-activedescendant": open && items[active] ? `${baseId}-o${active}` : undefined, autoComplete: "off", placeholder: labels.add, disabled: disabled, value: text, onChange: (event) => {
                            setText(event.target.value);
                            setOpen(true);
                            setActive(0);
                        }, onFocus: () => setOpen(true), onClick: () => setOpen(true), onBlur: () => window.setTimeout(() => setOpen(false), 120), onKeyDown: onKeyDown }), _jsxs("ul", { id: listId, role: "listbox", "aria-label": label, className: "tag-picker__list", hidden: !open, children: [items.map((item, index) => (_jsx("li", { id: `${baseId}-o${index}`, role: "option", "aria-selected": index === active, className: "tag-picker__option", 
                                // The mouse must not blur the input before the click lands.
                                onMouseDown: (event) => event.preventDefault(), onClick: () => choose(index), children: item.label }, item.key))), items.length === 0 ? (_jsx("li", { className: "tag-picker__empty", role: "presentation", children: labels.empty })) : null] })] })) : null] }));
}
//# sourceMappingURL=TagPicker.js.map